import * as React from 'react';
import { Link } from 'react-router-dom';
import type { AgentSource } from '@cdf/api-client-react';

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const UUID_ONLY = new RegExp(`^${UUID}$`, 'i');
/** Only server-issued record pages for these CRM kinds become inline links. */
const RECORD_LINK = new RegExp(`^/partner-engagement/(accounts|contacts|opportunities|enquiries)/${UUID}$`, 'i');
const NAME_PRIORITY: Record<string, number> = { contacts: 0, accounts: 1, opportunities: 2, enquiries: 3 };
const ENQUIRY_PRIORITY = 4;
const ENQUIRY_PHRASE = /(?<![\p{L}\p{N}])(?:discovery\s+(?:meeting|call|session)s?|enquir(?:y|ies))(?![\p{L}\p{N}])/giu;

export type AgentReplyLink = { start: number; end: number; to: string };
type Candidate = AgentReplyLink & { priority: number };

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function recordName(source: AgentSource): string | null {
  const identity = source.sourceIdentity || source.label;
  const separator = identity.indexOf(': ');
  const name = (separator >= 0 ? identity.slice(separator + 2) : identity).trim();
  if (name.length < 3 || name === 'matching record' || UUID_ONLY.test(name)) return null;
  return name;
}

function relatedContactLink(excerpt: string): string | null {
  const match = new RegExp(`"contact_id":"(${UUID})"`, 'i').exec(excerpt);
  return match ? `/partner-engagement/contacts/${match[1]}` : null;
}

function paragraphs(content: string): { start: number; end: number }[] {
  const ranges: { start: number; end: number }[] = [];
  let start = 0;
  for (const match of content.matchAll(/\n\s*\n/g)) {
    ranges.push({ start, end: match.index });
    start = match.index + match[0].length;
  }
  ranges.push({ start, end: content.length });
  return ranges;
}

/**
 * Finds record references in an Agent reply that can open the cited CRM record.
 * Names come from the reply's own citations; enquiry wording ("discovery
 * meeting", "enquiry") links only inside paragraphs that cite that enquiry.
 */
export function agentReplyLinks(content: string, sources: readonly AgentSource[]): AgentReplyLink[] {
  const candidates: Candidate[] = [];
  const blocks = paragraphs(content);
  for (const source of sources) {
    if (source.sourceType !== 'crm_record' || !source.deepLink) continue;
    const route = RECORD_LINK.exec(source.deepLink);
    if (!route) continue;
    const kind = route[1]!.toLowerCase();
    const name = recordName(source);
    if (name) {
      const to = kind === 'enquiries' ? relatedContactLink(source.excerpt) ?? source.deepLink : source.deepLink;
      const pattern = new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(name)}(?![\\p{L}\\p{N}])`, 'gu');
      for (const match of content.matchAll(pattern)) {
        candidates.push({ start: match.index, end: match.index + match[0].length, to, priority: NAME_PRIORITY[kind] ?? 3 });
      }
    }
    if (kind === 'enquiries') {
      const marker = `[${source.citation}]`;
      for (const block of blocks) {
        const text = content.slice(block.start, block.end);
        if (!text.includes(marker)) continue;
        for (const match of text.matchAll(ENQUIRY_PHRASE)) {
          const start = block.start + match.index;
          candidates.push({ start, end: start + match[0].length, to: source.deepLink, priority: ENQUIRY_PRIORITY });
        }
      }
    }
  }
  candidates.sort((a, b) => a.priority - b.priority || (b.end - b.start) - (a.end - a.start) || a.start - b.start);
  const chosen: AgentReplyLink[] = [];
  for (const candidate of candidates) {
    if (chosen.some(link => candidate.start < link.end && link.start < candidate.end)) continue;
    chosen.push({ start: candidate.start, end: candidate.end, to: candidate.to });
  }
  return chosen.sort((a, b) => a.start - b.start);
}

/** Agent reply text with cited accounts, contacts, opportunities and enquiries as links. */
export function AgentLinkedReply({ content, sources }: { content: string; sources?: readonly AgentSource[] | null }) {
  const links = React.useMemo(() => agentReplyLinks(content, sources ?? []), [content, sources]);
  if (links.length === 0) return <div className="whitespace-pre-wrap">{content}</div>;
  const parts: React.ReactNode[] = [];
  let offset = 0;
  for (const link of links) {
    parts.push(content.slice(offset, link.start));
    parts.push(<Link key={link.start} to={link.to} className="underline underline-offset-4 hover:text-muted-foreground">{content.slice(link.start, link.end)}</Link>);
    offset = link.end;
  }
  parts.push(content.slice(offset));
  return <div className="whitespace-pre-wrap">{parts}</div>;
}
